import { createElement, lazy, Suspense } from 'react';
import type { PageContext } from '../lib/types';
import { usesDesignV2 } from '../features/design-v2/rollout';
// Brands (IA §12–13, §21): global, cross-workspace context. Brand assets = filtered Assets view.
import { html, raw, toast } from '../lib/dom';
import { icon } from '../components/icons';
import { coverThumb, assetThumb } from '../components/thumb';
import { pageHead, tabs, projectCard, assetCard, kpi, empty, select, status } from '../components/ui';
import { listBrands, getBrand, brandProjects, brandWorkspaces, brandStats, getWorkspace, projectBrands, projectStats, createBrand, updateBrand, deleteBrand, currentUser } from '../api/workspace';
import { listAssets, assetCount, assetListState, loadMoreAssets, usage, ownerLabel } from '../api/library';
import { loadMoreMarker, bindLoadMore } from '../components/load-more';
import { plural } from '../lib/format';
import { openNewProject } from './projects';
import { openModal, confirmModal } from '../components/modal';
import { bindCoverEditors, coverEditor } from '../components/entity-cover';
import { setEntityCover } from '../api/entity-covers';
import { createBlankFlow } from '../api/flow-directory';

const BrandsPage = lazy(() => import('../features/design-v2/BrandsPage').then(module => ({ default: module.BrandsPage })));
const BrandDetailPage = lazy(() => import('../features/design-v2/BrandDetailPage').then(module => ({ default: module.BrandDetailPage })));

export function openNewBrand({ designV2 = false }: { designV2?: boolean } = {}) {
  openModal({
    title: 'New brand',
    body: html`<form class="form" data-brand-form>
      <div class="field"><label for="brand-name">Name</label><input class="input" id="brand-name" name="name" maxlength="255" placeholder="e.g. Bots & Bones" required autofocus></div>
      <div class="field"><label for="brand-description">Description</label><textarea class="textarea" id="brand-description" name="description" rows="3"></textarea></div>
      <div class="field"><label for="brand-image">Image</label><input class="input" id="brand-image" name="image" type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml,.svg"><span class="muted">JPEG, PNG, WebP or SVG · up to 20 MB</span></div>
      <p class="banner error" role="alert" data-brand-error hidden></p>
    </form>`,
    foot: html`<button class="btn ghost" data-close>Cancel</button><button class="btn primary" data-create-brand>Create brand</button>`,
    onMount(modal: HTMLElement, close: () => void) {
      const form = modal.querySelector<HTMLFormElement>('[data-brand-form]')!;
      const button = modal.querySelector<HTMLButtonElement>('[data-create-brand]')!;
      const error = modal.querySelector<HTMLElement>('[data-brand-error]')!;
      const submit = async (event: Event) => {
        event.preventDefault();
        if (button.disabled || !form.reportValidity()) return;
        button.disabled = true;
        button.textContent = 'Creating…';
        error.hidden = true;
        try {
          const fields = new FormData(form);
          const id = await createBrand({
            name: String(fields.get('name') || ''),
            description: String(fields.get('description') || ''),
          });
          const image = fields.get('image');
          if (image instanceof File && image.size) {
            try { await setEntityCover('brand', id, image); }
            catch (cause) {
              toast(cause instanceof Error ? `Brand created, but image was not saved: ${cause.message}` : 'Brand created, but image was not saved.');
            }
          }
          close();
          location.hash = `/brands/${id}${designV2 ? '?design=v2' : ''}`;
        } catch (cause) {
          error.textContent = cause instanceof Error ? cause.message : 'Could not create brand.';
          error.hidden = false;
        } finally {
          button.disabled = false;
          button.textContent = 'Create brand';
        }
      };
      form.addEventListener('submit', submit);
      button.addEventListener('click', submit);
    },
  });
}

export function renderList(context: PageContext) {
  if (usesDesignV2(context)) return { title: 'Brands', content: createElement(Suspense, { fallback: createElement('div', { role: 'status', className: 'page' }, 'Loading brands…') }, createElement(BrandsPage, { context, onCreate: () => openNewBrand({ designV2: true }) })) };
  const brands = listBrands();
  const body = html`<div class="page">
    ${pageHead({ title: 'Brands', lede: 'Brands live at the organization level and can be used in any workspace and project. Their logos, characters and prompts are stored in Assets.', actions: currentUser()?.role === 'admin' ? html`<button class="btn primary" data-new-brand>${icon('plus')}New brand</button>` : '' })}
    <div class="grid c3">${brands.map((b?: any) => {
      const s = brandStats(b.id);
      return html`<a class="card hover entity-card" href="#/brands/${b.id}">
        ${coverThumb(b)}
        <div class="body" style="gap:10px">
          <div class="row"><span class="title" style="font-size:17px">${b.name}</span><span class="spacer"></span>${status(b.status)}</div>
          <p class="muted" style="font-size:13px">${b.description}</p>
          <div class="swatches">${b.palette.map((c?: any) => html`<span class="swatch" style="width:20px;height:20px;background:${raw(c)}"></span>`)}</div>
          <div class="meta"><span>${plural(s.projects, 'project')}</span><span>·</span><span>${plural(s.workspaces, 'workspace')}</span><span>·</span><span>${s.assets} brand assets</span></div>
        </div></a>`;
    })}</div>
  </div>`;
  return { title: 'Brands', body, mount(root: HTMLElement) {
    root.querySelector('[data-new-brand]')?.addEventListener('click', () => openNewBrand());
  } };
}

export function renderDetail(ctx: PageContext) {
  if (usesDesignV2(ctx)) return {
    title: getBrand(ctx.params.brandId)?.name || 'Brand',
    content: createElement(Suspense, { fallback: createElement('div', { role: 'status', className: 'page' }, 'Loading brand…') }, createElement(BrandDetailPage, {
      context: ctx,
      onNewProject: () => { void openNewProject({ brandId: ctx.params.brandId, designV2: true }); },
      onNewFlow: (projectId: string) => {
        const projects = brandProjects(ctx.params.brandId);
        const start = async (targetProjectId: string) => {
          try {
            const flow = await createBlankFlow({ projectId: targetProjectId, name: 'New Flow' });
            location.hash = `/projects/${encodeURIComponent(targetProjectId)}/flows/${encodeURIComponent(flow.id || '')}`;
          } catch (cause) { toast(cause instanceof Error ? cause.message : 'Could not create flow'); }
        };
        if (projectId) { void start(projectId); return; }
        if (projects.length === 1) { void start(projects[0].id); return; }
        openModal({
          title: 'New flow',
          body: html`<form class="form" data-brand-flow-form><div class="field"><label for="brand-flow-project">Project</label><select class="select" id="brand-flow-project" name="project" required>${projects.map((project?: any) => html`<option value="${project.id}">${project.name}</option>`)}</select></div></form>`,
          foot: html`<button class="btn ghost" data-close>Cancel</button><button class="btn primary" data-create-brand-flow>Create flow</button>`,
          onMount(modal: HTMLElement, close: () => void) {
            modal.querySelector('[data-create-brand-flow]')?.addEventListener('click', () => {
              const form = modal.querySelector<HTMLFormElement>('[data-brand-flow-form]');
              if (!form?.reportValidity()) return;
              const projectId = String(new FormData(form).get('project') || '');
              if (projectId) void start(projectId).then(close);
            });
          },
        });
      },
    })),
  };
  const b = getBrand(ctx.params.brandId);
  if (!b) return { title: 'Not found', body: html`<div class="page">${empty('Brand not found')}</div>` };
  const tab = ['overview', 'assets', 'projects', 'settings'].includes(ctx.query.tab) ? ctx.query.tab : 'overview';
  const s = brandStats(b.id);
  const projects = brandProjects(b.id);
  const workspaces = brandWorkspaces(b.id);
  // Overview uses the newest brand-owned assets; the Assets tab pages through everything.
  const own = tab === 'overview' ? listAssets({ brandId: b.id, scope: 'BRAND', limit: 100 }) : [];
  const assetFilters = { brandId: b.id, scope: ctx.query.scope, q: ctx.query.q };
  const all = tab === 'assets' ? listAssets(assetFilters) : [];
  const mainCharacter = own.find((a?: any) => a.type === 'CHARACTER' && a.current_version > 1) || own.find((a?: any) => a.type === 'CHARACTER');

  const content: any = {
    overview: html`<div class="grid c4">${kpi('Projects', s.projects)}${kpi('Workspaces', s.workspaces, workspaces.map((w?: any) => w.name).join(' · '))}${kpi('Brand assets', s.assets, `${assetCount({ brandId: b.id, scope: 'PROJECT' }) ?? '…'} more in projects`)}${kpi('Runs', s.runs, `${s.flows} flows`)}</div>
      <div class="split">
        <div class="col" style="gap:24px">
          <section class="section"><div class="section-head"><h2>Projects</h2><a class="link" href="#/brands/${b.id}?tab=projects">All →</a></div><div class="grid c2">${projects.slice(0, 4).map((p?: any) => projectCard(p, { workspace: getWorkspace(p.workspace_id), brands: projectBrands(p.id), stats: projectStats(p.id) }))}</div></section>
          <section class="section"><div class="section-head"><h2>Characters</h2><a class="link" href="#/brands/${b.id}?tab=assets">Assets →</a></div><div class="grid c4">${own.filter((a?: any) => a.type === 'CHARACTER').slice(0, 8).map((a?: any) => assetCard(a, a.status))}</div></section>
        </div>
        <div class="col" style="gap:16px">
          ${mainCharacter ? html`<a class="card hover" href="#/assets/${mainCharacter.id}" style="overflow:hidden">${assetThumb(mainCharacter, 'thumb hero')}<div style="padding:12px 14px"><div class="eyebrow">Main character</div><div style="font-weight:500">${mainCharacter.name}</div><div class="mono muted" style="font-size:11px">used in ${plural(usage(mainCharacter.id).flows, 'flow')}</div></div></a>` : ''}
          <section class="card pad col" style="gap:10px"><h3>Palette</h3><div class="swatches">${b.palette.map((c?: any) => html`<div class="col" style="gap:3px;align-items:center"><span class="swatch" style="width:40px;height:40px;background:${raw(c)}"></span><span class="mono muted" style="font-size:9.5px">${c}</span></div>`)}</div>
            <h3 style="margin-top:6px">Fonts</h3><div>${b.fonts.map((f?: any) => html`<div style="font-size:16px">${f}</div>`)}</div></section>
          <section class="card pad col" style="gap:8px"><h3>Workspaces</h3>${workspaces.map((w?: any) => html`<a class="list-row" href="#/workspaces/${w.id}"><span class="avatar sm" style="background:var(--moss);color:var(--on-accent)">${w.name[0]}</span><span class="t">${w.name}</span></a>`)}</section>
        </div>
      </div>`,
    assets: html`<div class="toolbar">
        <label class="search">${icon('search')}<input class="input sm" data-filter="q" value="${ctx.query.q || ''}" placeholder="Search ${b.name} assets…"></label>
        ${select('scope', [['BRAND', 'Brand-owned'], ['PROJECT', 'From brand projects']], ctx.query.scope, { placeholder: 'All connected to brand' })}
        <span class="spacer"></span><a class="link mono" style="font-size:11px;color:var(--fern)" href="#/assets?brand=${b.id}">Open in Assets →</a></div>
      <div class="grid auto-sm">${all.map((a?: any) => assetCard(a, ownerLabel(a)))}</div>${tab === 'assets' ? loadMoreMarker(assetListState(assetFilters), 'Loading assets…') : ''}`,
    projects: html`<div class="toolbar"><span class="spacer"></span><button class="btn primary" data-new-project>${icon('plus')}New ${b.name} project</button></div><div class="grid auto">${projects.map((p?: any) => projectCard(p, { workspace: getWorkspace(p.workspace_id), brands: projectBrands(p.id), stats: projectStats(p.id) }))}</div>`,
    settings: html`<div class="card pad-lg"><form class="form" data-brand-settings>
      <div class="field"><label for="brand-settings-name">Name</label><input class="input" id="brand-settings-name" name="name" maxlength="255" value="${b.name}" required></div>
      <div class="field"><label for="brand-settings-description">Description</label><textarea class="textarea" id="brand-settings-description" name="description" rows="2">${b.description}</textarea></div>
      <div><button class="btn primary" type="submit">Save</button></div><p class="banner error" role="alert" data-settings-error hidden></p></form></div>`,
  };

  const body = html`<div class="page">
    <a data-back class="btn ghost sm" href="#/brands" style="align-self:flex-start">${icon('back')}Back</a>
    <header class="page-head"><div class="row" style="gap:16px">${currentUser()?.role === 'admin' ? coverEditor(b, 'brand', 'cover-editor-compact') : coverThumb(b, 'thumb')}<div><div class="eyebrow">Brand</div><h1>${b.name} </h1><p class="lede" style="margin-top:4px">${b.description}</p></div></div><div class="row">${status(b.status)}${currentUser()?.role === 'admin' ? html`<button class="btn danger" data-delete-brand>${icon('trash')}Delete brand</button>` : ''}</div></header>
    ${tabs([['overview', 'Overview'], ['assets', 'Assets', assetCount({ brandId: b.id }) ?? '…'], ['projects', 'Projects', projects.length], ['settings', 'Settings']], tab)}
    ${content[tab]}
  </div>`;

  return {
    title: b.name, body,
    mount(root?: any) {
      const t = root.querySelector('.page-head .thumb');
      if (t) Object.assign(t.style, { width: '76px', height: '76px', borderRadius: '10px', flex: 'none' });
      bindCoverEditors(root, ctx.rerender);
      root.querySelector('[data-new-project]')?.addEventListener('click', (event: Event) => openNewProject({ brandId: b.id, returnFocus: event.currentTarget as HTMLElement }));
      const form = root.querySelector('[data-brand-settings]');
      form?.addEventListener('submit', async (event: Event) => {
        event.preventDefault();
        const button = form.querySelector('button[type="submit"]');
        const error = form.querySelector('[data-settings-error]');
        button.disabled = true;
        error.hidden = true;
        try {
          const values = new FormData(form);
          await updateBrand(b.id, {name: String(values.get('name') || ''), description: String(values.get('description') || '')});
          toast('Brand saved');
          ctx.rerender();
        } catch (cause) {
          error.textContent = cause instanceof Error ? cause.message : 'Could not save brand';
          error.hidden = false;
        } finally { button.disabled = false; }
      });
      root.querySelector('[data-delete-brand]')?.addEventListener('click', () => confirmModal({
        title: `Delete “${b.name}”?`,
        text: 'This permanently deletes the brand and removes its user memberships. Active projects, workspaces and assets must be detached first. Archived projects are kept and will have no brand if restored. This cannot be undone.',
        confirmLabel: 'Delete brand', danger: true,
        onConfirm: async () => {
          await deleteBrand(b.id);
          toast('Brand deleted');
          location.hash = '/brands';
        },
      }));
      return tab === 'assets' ? bindLoadMore(root, assetListState(assetFilters), () => loadMoreAssets(assetFilters)) : undefined;
    },
  };
}
